/**
 * OurTeamPage — public-facing introduction to the AI Operations Division.
 *
 * Layout mirrors the JapanETCcard / JapanTollReceipts sister-site pattern
 * (Director + QA Lead row, then 3 Senior Analysts row) but is themed for
 * NoJo Enterprise's corporate identity and frames the team across all
 * three operated services rather than a single product.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Cpu,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import Reveal from "@/components/motion/Reveal";
import BrandGraphic from "@/components/motion/BrandGraphic";
import PeopleStoryTabs from "@/components/PeopleStoryTabs";
import { AI_TEAM, TEAM_GROUP_PHOTOS, TEAM_MANIFESTO, ROLE_THEME } from "@/data/aiTeam";

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

function MemberCard({ member, lang, featured }) {
  const isJa = lang === "ja";
  const theme = ROLE_THEME[member.role];
  return (
    <div
      data-testid={`our-team-card-${member.slug}`}
      className={`relative rounded-2xl bg-white text-slate-900 ring-1 ring-slate-200 overflow-hidden transition-all hover:-translate-y-0.5 hover:ring-slate-300 hover:shadow-[0_18px_40px_-24px_rgba(15,23,42,0.18)] ${
        featured ? "p-6 md:p-7" : "p-5 md:p-6"
      }`}
    >
      <span
        aria-hidden
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ backgroundColor: theme.hex }}
      />
      <div className={featured ? "flex flex-col md:flex-row gap-6" : "flex flex-col items-center text-center"}>
        {/* Portrait */}
        <div className={featured ? "shrink-0 md:w-[200px]" : "w-full max-w-[200px] mb-4"}>
          <div className="relative rounded-xl overflow-hidden ring-1 ring-slate-200 bg-slate-100">
            <img
              src={member.portrait}
              alt={`${pickL(member.nameEn, member.nameJa, lang)} — ${pickL(member.titleEn, member.titleJa, lang)}`}
              className="block w-full h-auto aspect-square object-cover"
              loading="lazy"
              draggable={false}
            />
            <span
              className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[9.5px] tracking-[0.22em] font-mono uppercase font-bold px-2.5 py-0.5 rounded-full text-white shadow-lg"
              style={{ backgroundColor: theme.hex }}
            >
              {isJa ? theme.labelJa : theme.labelEn}
            </span>
          </div>
        </div>

        {/* Bio */}
        <div className={featured ? "flex-1 min-w-0" : "w-full"}>
          <div className={featured ? "text-left" : "text-center"}>
            <h3 className="text-[22px] md:text-[24px] font-bold tracking-tight text-slate-900">
              {pickL(member.nameEn, member.nameJa, lang)}
              <span className="ml-2 text-[14px] font-normal text-slate-400">
                {pickL(member.nameJa, member.nameEn, lang)}
              </span>
            </h3>
            <div
              className="mt-1 text-[13px] font-semibold"
              style={{ color: theme.hex }}
            >
              {pickL(member.titleEn, member.titleJa, lang)}
            </div>
            {member.specEn && (
              <div className="mt-0.5 text-[11.5px] tracking-tight text-slate-500">
                {pickL(member.specEn, member.specJa, lang)}
              </div>
            )}
          </div>

          <p className={`mt-4 text-[13.5px] leading-[1.75] text-slate-600 ${featured ? "text-left" : "text-left"}`}>
            {pickL(member.bioEn, member.bioJa, lang)}
          </p>

          <ul className="mt-4 space-y-2">
            {(isJa ? member.bulletsJa : member.bulletsEn).map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-[12.5px] text-slate-700 leading-snug">
                <Sparkles
                  className="h-3 w-3 mt-1 shrink-0"
                  style={{ color: theme.hex }}
                  strokeWidth={2.5}
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function GroupPhotoMarquee({ lang }) {
  const isJa = lang === "ja";
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % TEAM_GROUP_PHOTOS.length), 5000);
    return () => clearInterval(id);
  }, []);
  const photo = TEAM_GROUP_PHOTOS[active];
  return (
    <div
      className="relative w-full aspect-[16/10] md:aspect-[16/9] rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-slate-100"
      data-testid="our-team-hero-photo"
    >
      {TEAM_GROUP_PHOTOS.map((p, i) => (
        <img
          key={p.id}
          src={p.src}
          alt={p.captionEn || "NoJo Enterprise AI Operations team"}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: i === active ? 1 : 0 }}
          loading={i === 0 ? "eager" : "lazy"}
        />
      ))}
      {/* Subtle neutral darkening only at the very bottom so the location chip
          stays legible — no purple tint over the photo. */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-slate-900/45 to-transparent"
      />
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white animate-brand-blink" />
          <span className="text-[10.5px] tracking-[0.22em] font-mono uppercase font-bold">
            {pickL(photo.locationEn, photo.locationJa, lang)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {TEAM_GROUP_PHOTOS.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActive(i)}
              aria-label={pickL(p.locationEn, p.locationJa, lang)}
              data-testid={`our-team-hero-dot-${p.id}`}
              className={`h-1.5 rounded-full transition-all ${
                i === active ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function OurTeamPage({ lang }) {
  const isJa = lang === "ja";
  const director = AI_TEAM.find((m) => m.role === "director");
  const qa = AI_TEAM.find((m) => m.role === "qa");
  const analysts = AI_TEAM.filter((m) => m.role === "analyst");

  return (
    <article className="bg-white text-slate-900" data-testid="our-team-page">
      <SEO {...PAGE_SEO.ourTeam[lang]} path={`/${lang}/our-team`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "AIチーム" : "Our AI Team", url: `/${lang}/our-team` },
        ])}
      />

      <PeopleStoryTabs lang={lang} />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-white">
        <BrandGraphic
          variant="wave"
          color="#7AB8E8"
          className="absolute inset-0 w-full h-full opacity-[0.08] pointer-events-none"
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal as="div" variant="up" className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 ring-1 ring-purple-200 mb-5">
              <Cpu className="h-3.5 w-3.5 text-[#663399]" strokeWidth={2.25} />
              <span className="text-[10.5px] tracking-[0.26em] font-mono uppercase font-bold text-[#663399]">
                {pickL(TEAM_MANIFESTO.eyebrowEn, TEAM_MANIFESTO.eyebrowJa, lang)}
              </span>
            </div>
            <h1
              className={[
                isJa ? "font-ja" : "font-display",
                "text-slate-900 text-4xl md:text-5xl lg:text-[60px] font-bold tracking-tighter leading-[1.04]",
              ].join(" ")}
            >
              {pickL(TEAM_MANIFESTO.titleEn, TEAM_MANIFESTO.titleJa, lang)}
            </h1>
            <p className="mt-5 text-[16px] md:text-[17px] leading-[1.75] text-slate-600 max-w-2xl">
              {pickL(TEAM_MANIFESTO.leadEn, TEAM_MANIFESTO.leadJa, lang)}
            </p>

            <ul className="mt-7 grid grid-cols-3 gap-4 max-w-md">
              <li className="border-l-2 border-[#F59E0B] pl-3">
                <div className="text-[22px] font-bold tracking-tight text-slate-900">1</div>
                <div className="text-[10px] tracking-[0.18em] font-mono uppercase text-slate-500 leading-snug">
                  {isJa ? "ディレクター" : "Director"}
                </div>
              </li>
              <li className="border-l-2 border-[#0E5FB0] pl-3">
                <div className="text-[22px] font-bold tracking-tight text-slate-900">1</div>
                <div className="text-[10px] tracking-[0.18em] font-mono uppercase text-slate-500 leading-snug">
                  {isJa ? "QA リード" : "QA lead"}
                </div>
              </li>
              <li className="border-l-2 border-[#2F8F3C] pl-3">
                <div className="text-[22px] font-bold tracking-tight text-slate-900">3</div>
                <div className="text-[10px] tracking-[0.18em] font-mono uppercase text-slate-500 leading-snug">
                  {isJa ? "AI アナリスト" : "AI analysts"}
                </div>
              </li>
            </ul>
          </Reveal>
          <Reveal as="div" variant="up" delay={150} className="lg:col-span-6">
            <GroupPhotoMarquee lang={lang} />
          </Reveal>
        </div>
      </section>

      {/* ===== HOW THE TEAM WORKS ===== */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="our-team-model">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-14">
          <div className="max-w-2xl mb-8">
            <div className="text-[10px] tracking-[0.28em] font-mono uppercase font-bold text-[#663399] mb-2">
              {isJa ? "運営モデル" : "How they work"}
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-slate-900 text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
              ].join(" ")}
            >
              {isJa
                ? "3つのアナリストロール・1つのQAロール・1つのディレクターロール。すべての自動化を同じ確認基準で支えます。"
                : "3 analysts · 1 QA lead · 1 director. The same discipline behind every automated task."}
            </h2>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-3">
            {[
              {
                step: "01",
                en: { t: "Analysts process", b: "Yuki, Hana and Aoi extract, interpret and classify the work — each focused on their specialty." },
                ja: { t: "アナリストが処理", b: "ユキ・ハナ・アオイがそれぞれの専門領域で抽出・解釈・分類を行います。" },
                color: "#2F8F3C",
              },
              {
                step: "02",
                en: { t: "QA validates", b: "Rin cross-checks every output and routes edge cases for human review — never auto-conclusions." },
                ja: { t: "QA が検証", b: "リンが出力を相互チェックし、判断が必要なケースを人によるレビューに回します（自動判断はしません）。" },
                color: "#0E5FB0",
              },
              {
                step: "03",
                en: { t: "Director approves", b: "Sora gives the final go-ahead before anything reaches a customer or external system." },
                ja: { t: "ディレクターが承認", b: "ソラがお客様や外部システムに届く前に最終承認を行います。" },
                color: "#F59E0B",
              },
              {
                step: "04",
                en: { t: "Customer ships", b: "Reports, registrations and records reach inboxes with the same discipline every time." },
                ja: { t: "お客様へ届く", b: "レポート・登録・記録が、毎回同じ規律でお客様の元に届きます。" },
                color: "#7AB8E8",
              },
            ].map((s, i) => {
              const c = isJa ? s.ja : s.en;
              return (
                <Reveal as="li" variant="up" delay={i * 80} key={s.step}>
                  <div className="relative h-full rounded-xl bg-white ring-1 ring-slate-200 p-5 hover:ring-slate-300 hover:shadow-[0_18px_40px_-24px_rgba(15,23,42,0.18)] transition-all">
                    <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: s.color }} />
                    <div
                      className="text-[10.5px] tracking-[0.22em] font-mono uppercase font-bold mb-3"
                      style={{ color: s.color }}
                    >
                      {isJa ? `ステップ ${s.step}` : `Step ${s.step}`}
                    </div>
                    <h3 className="text-[16px] font-bold tracking-tight text-slate-900">{c.t}</h3>
                    <p className="mt-2 text-[12.5px] leading-[1.7] text-slate-600">{c.b}</p>
                  </div>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ===== LEADERSHIP ROW (Director + QA Lead) ===== */}
      <section className="bg-white border-b border-slate-200" data-testid="our-team-leadership">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="max-w-2xl mb-8">
            <div className="text-[10px] tracking-[0.28em] font-mono uppercase font-bold text-[#663399] mb-2">
              {isJa ? "リーダーシップ" : "Leadership"}
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-slate-900 text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
              ].join(" ")}
            >
              {isJa
                ? "最終確認と品質ゲートを支える2つのロール。"
                : "The two people gating every output that leaves NoJo."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Reveal as="div" variant="up">
              <MemberCard member={director} lang={lang} featured />
            </Reveal>
            <Reveal as="div" variant="up" delay={120}>
              <MemberCard member={qa} lang={lang} featured />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== ANALYSTS ROW ===== */}
      <section className="bg-slate-50 border-b border-slate-200" data-testid="our-team-analysts">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="max-w-2xl mb-8">
            <div className="text-[10px] tracking-[0.28em] font-mono uppercase font-bold text-[#2F8F3C] mb-2">
              {isJa ? "シニアAIアナリスト" : "Senior AI Analysts"}
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-slate-900 text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
              ].join(" ")}
            >
              {isJa
                ? "それぞれが異なる強みを持つ3つのアナリストロール。"
                : "Three analysts. Three different strengths."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {analysts.map((m, i) => (
              <Reveal as="div" variant="up" delay={i * 100} key={m.slug}>
                <MemberCard member={m} lang={lang} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRUST BAND ===== */}
      <section className="bg-white border-b border-slate-200" data-testid="our-team-trust">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-xl bg-white ring-1 ring-slate-200 p-5 hover:ring-slate-300 hover:shadow-[0_18px_40px_-24px_rgba(15,23,42,0.18)] transition-all">
            <ShieldCheck className="h-7 w-7 text-[#663399] mb-3" strokeWidth={1.75} />
            <h3 className="text-[15px] font-bold text-slate-900">
              {isJa ? "最終判断は常に人が行います" : "Final judgment stays with people"}
            </h3>
            <p className="mt-2 text-[12.5px] leading-[1.7] text-slate-600">
              {isJa
                ? "AIは整理と確認を支援します。お客様への影響がある判断は自動化されず、必ず人によるレビューを経由します。"
                : "AI assists with extraction and verification. Decisions that affect a customer are never auto-concluded — they always pass through human review."}
            </p>
          </div>
          <div className="rounded-xl bg-white ring-1 ring-slate-200 p-5 hover:ring-slate-300 hover:shadow-[0_18px_40px_-24px_rgba(15,23,42,0.18)] transition-all">
            <Clock className="h-7 w-7 text-[#0E5FB0] mb-3" strokeWidth={1.75} />
            <h3 className="text-[15px] font-bold text-slate-900">
              {isJa ? "24時間運用、JST基準" : "Always-on operations · JST aligned"}
            </h3>
            <p className="mt-2 text-[12.5px] leading-[1.7] text-slate-600">
              {isJa
                ? "通行記録の取得、ETCの請求処理、レポート配信のすべてが日本時間（JST）に合わせて自動的に実行されます。"
                : "Toll-record pulls, ETC billing cycles, and report delivery all run automatically — aligned to Japan Standard Time."}
            </p>
          </div>
          <div className="rounded-xl bg-white ring-1 ring-slate-200 p-5 hover:ring-slate-300 hover:shadow-[0_18px_40px_-24px_rgba(15,23,42,0.18)] transition-all">
            <Users className="h-7 w-7 text-[#2F8F3C] mb-3" strokeWidth={1.75} />
            <h3 className="text-[15px] font-bold text-slate-900">
              {isJa ? "3つのサービスを横断" : "Across all three services"}
            </h3>
            <p className="mt-2 text-[12.5px] leading-[1.7] text-slate-600">
              {isJa
                ? "同じチームが、JapanETCcard・JapanTollReceipts・準備中のJapanChangeMoneyを支えています。サービスごとの一貫性が、自然に生まれます。"
                : "The same team supports JapanETCcard, JapanTollReceipts, and the planned JapanChangeMoney service — so consistency across services happens by design."}
            </p>
          </div>
        </div>
      </section>

      {/* ===== CTA BAND — kept as the one strong-color flourish at page end ===== */}
      <section className="relative bg-[#5B2E83]">
        <BrandGraphic
          variant="orbit"
          color="#7AB8E8"
          className="absolute top-0 right-0 w-44 h-44 opacity-40 pointer-events-none hidden md:block"
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.28em] font-mono uppercase font-bold text-[#7AB8E8] mb-3">
              {isJa ? "AI支援ワークフローが支えるサービス" : "Services powered by the team"}
            </div>
            <h3
              className={[
                isJa ? "font-ja" : "font-display",
                "text-white text-2xl md:text-[32px] font-bold tracking-tight leading-snug",
              ].join(" ")}
            >
              {isJa
                ? "ETC、通行記録、そして両替まで ─ どのサービスでも同じチーム。"
                : "ETC, toll records, planned exchange — same team, every service."}
            </h3>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link
              to={`/${lang}/services`}
              data-testid="our-team-cta-services"
              className="inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-5 py-3 rounded-full text-[13px] font-semibold hover:bg-[#F1F5F9] transition-colors"
            >
              {isJa ? "サービスを見る" : "Explore services"}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to={`/${lang}/contact`}
              data-testid="our-team-cta-contact"
              className="inline-flex items-center gap-2 bg-transparent text-white ring-1 ring-white/30 px-5 py-3 rounded-full text-[13px] font-semibold hover:bg-white/10 transition-colors"
            >
              {isJa ? "お問い合わせ" : "Contact us"}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center gap-2 text-[11px] text-white/55">
            <CheckCircle2 className="h-3.5 w-3.5 text-[#2F8F3C]" strokeWidth={2.25} />
            <span>
              {isJa
                ? "AIによる支援は判断を保証するものではありません。最終判断は常に人が行います。"
                : "AI assists with extraction and verification — it does not guarantee outcomes. Final judgment always stays with a person."}
            </span>
          </div>
        </div>
      </section>
    </article>
  );
}
